import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Trash2, Plus, Upload, RefreshCw, Download, Filter, Undo2, Pencil, Truck, Sparkles, Check, Clock, XCircle } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import { downloadBlob } from "@/lib/csv";
import ImportCSVDialog from "@/components/admin/ImportCSVDialog";
import BulkUpdateDialog from "@/components/admin/BulkUpdateDialog";
import OrderDetailDialog from "@/components/admin/OrderDetailDialog";
import EditBookDialog from "@/components/admin/EditBookDialog";
import BulkTrackingDialog from "@/components/admin/BulkTrackingDialog";
import BulkDeleteDialog from "@/components/admin/BulkDeleteDialog";
import AdminCaseDetailDialog from "@/components/admin/AdminCaseDetailDialog";
import GmcFeedPanel from "@/components/admin/GmcFeedPanel";
import IngramSyncPanel from "@/components/admin/IngramSyncPanel";
import ShippingBulkPanel from "@/components/admin/ShippingBulkPanel";
import ReviewsPanel from "@/components/admin/ReviewsPanel";
import CategoryManager from "@/components/admin/CategoryManager";

export default function Admin() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("books");
  const [books, setBooks] = useState([]);
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState({ total_orders: 0, total_books: 0, total_revenue: 0 });
  const [creating, setCreating] = useState(false);
  const [importing, setImporting] = useState(false);
  const [bulkUpdating, setBulkUpdating] = useState(false);

  // Catalog search. Server-side: the backend /books route already supports ?q=
  // across title, author, isbn, publisher and category, plus sku for admins.
  // Filtering client-side would only ever search the current page, which is
  // useless against a six-figure catalogue.
  const [bookSearch, setBookSearch] = useState("");
  const bookSearchReady = useRef(false);

  useEffect(() => {
    // Skip the first run so we do not duplicate the initial refresh() fetch.
    if (!bookSearchReady.current) {
      bookSearchReady.current = true;
      return;
    }
    const term = bookSearch.trim();
    const timer = setTimeout(async () => {
      try {
        const r = await api.get("/books", { params: term ? { q: term } : {} });
        setBooks(Array.isArray(r.data) ? r.data : r.data?.items || []);
      } catch (e) {
        toast.error("Search failed");
      }
    }, 350);
    return () => clearTimeout(timer);
  }, [bookSearch]);
  const defaultDates = () => {
    const today = new Date();
    const start = new Date();
    start.setDate(today.getDate() - 30);
    const fmt = (d) => d.toISOString().slice(0, 10);
    return { start_date: fmt(start), end_date: fmt(today) };
  };
  const [orderFilters, setOrderFilters] = useState({ q: "", status: "all", ...defaultDates() });
  const [selectedOrders, setSelectedOrders] = useState(new Set());
  const [exporting, setExporting] = useState(false);
  const [refundingId, setRefundingId] = useState(null);
  const [detailOrderId, setDetailOrderId] = useState(null);
  const [editingBook, setEditingBook] = useState(null);
  const [exportingBooks, setExportingBooks] = useState(false);
  const [bulkTracking, setBulkTracking] = useState(false);
  const [bulkDelete, setBulkDelete] = useState(false);
  // ----- Cases tab state -----
  const [cases, setCases] = useState([]);
  const [openCasesCount, setOpenCasesCount] = useState(0);
  const [caseFilters, setCaseFilters] = useState({ type: "all", status: "all", is_open: "open" });
  const [selectedCaseId, setSelectedCaseId] = useState(null);
  const emptyForm = {
    title: "", author: "", description: "", price: "", cover_image: "",
    category: "Literary Fiction", formats: "Paperback", stock: 100, featured: false,
    sku: "", language: "", condition: "", pages: "", isbn: "",
  };
  const [form, setForm] = useState(emptyForm);

  useEffect(() => {
    if (loading) return;
    if (!user || user.role !== "admin") {
      toast.error("Admin only");
      nav("/login");
    } else {
      refresh();
    }
    // eslint-disable-next-line
  }, [user, loading]);

  const buildOrderParams = (filters = orderFilters) => {
    const p = {};
    if (filters.q) p.q = filters.q;
    if (filters.status && filters.status !== "all") p.status = filters.status;
    if (filters.start_date) p.start_date = filters.start_date;
    if (filters.end_date) p.end_date = filters.end_date;
    return p;
  };

  const refresh = async () => {
    const [b, o, s] = await Promise.all([
      api.get("/books"),
      api.get("/admin/orders", { params: buildOrderParams() }),
      api.get("/admin/stats"),
    ]);
    setBooks(b.data);
    setOrders(o.data);
    setStats(s.data);
    setSelectedOrders(new Set());
  };

  const refreshOrders = async (filters) => {
    const { data } = await api.get("/admin/orders", { params: buildOrderParams(filters) });
    setOrders(data);
    setSelectedOrders(new Set());
  };

  const buildCaseParams = (filters = caseFilters) => {
    const p = {};
    if (filters.type && filters.type !== "all") p.type = filters.type;
    if (filters.status && filters.status !== "all") p.status = filters.status;
    if (filters.is_open === "open") p.is_open = true;
    if (filters.is_open === "closed") p.is_open = false;
    return p;
  };

  const refreshCases = async (filters = caseFilters) => {
    const { data } = await api.get("/admin/cases", { params: buildCaseParams(filters) });
    setCases(data);
  };

  // Refresh openCasesCount whenever cases change (fast path)
  useEffect(() => {
    if (!user || user.role !== "admin") return;
    api.get("/admin/cases", { params: { is_open: true } })
      .then((r) => setOpenCasesCount(r.data.length))
      .catch(() => {});
    // eslint-disable-next-line
  }, [user, cases.length]);

  useEffect(() => {
    if (tab === "cases") refreshCases(caseFilters);
    // eslint-disable-next-line
  }, [tab]);

  const clearOrderFilters = () => {
    const cleared = { q: "", status: "all", start_date: "", end_date: "" };
    setOrderFilters(cleared);
    refreshOrders(cleared);
  };

  const resetTo30Days = () => {
    const next = { q: "", status: "all", ...defaultDates() };
    setOrderFilters(next);
    refreshOrders(next);
  };

  const refundOrder = async (order) => {
    const confirmed = window.confirm(
      `Refund ${order.email || "guest"} for $${(order.amount || 0).toFixed(2)}?\nThis creates a full refund on Stripe and cannot be undone.`
    );
    if (!confirmed) return;
    setRefundingId(order.order_id);
    try {
      const { data } = await api.post(`/admin/orders/${order.order_id}/refund`, {
        reason: "requested_by_customer",
      });
      toast.success(`Refunded $${data.amount.toFixed(2)}`);
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Refund failed");
    } finally {
      setRefundingId(null);
    }
  };

  const toggleOrder = (id) => {
    setSelectedOrders((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleAllOrders = () => {
    setSelectedOrders((prev) => (prev.size === orders.length ? new Set() : new Set(orders.map((o) => o.order_id))));
  };

  const exportOrders = async (scope, format) => {
    // scope: 'all' | 'filtered' | 'selected'
    if (scope === "selected" && selectedOrders.size === 0) {
      toast.error("Select at least one order first");
      return;
    }
    setExporting(true);
    try {
      const params = { format };
      if (scope === "filtered") Object.assign(params, buildOrderParams());
      if (scope === "selected") params.order_ids = Array.from(selectedOrders).join(",");
      const resp = await api.get("/admin/orders/export", {
        params,
        responseType: "blob",
      });
      const stamp = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
      const ext = format === "xlsx" ? "xlsx" : "csv";
      downloadBlob(resp.data, `orders-${scope}-${stamp}.${ext}`);
      toast.success(`Exported ${scope} orders as ${ext.toUpperCase()}`);
    } catch (e) {
      toast.error("Export failed");
    } finally {
      setExporting(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form };
      payload.price = parseFloat(form.price);
      // Numeric optionals: strip if empty, coerce otherwise
      if (form.pages === "" || form.pages === null || form.pages === undefined) {
        delete payload.pages;
      } else {
        const p = parseInt(form.pages, 10);
        if (Number.isFinite(p)) payload.pages = p; else delete payload.pages;
      }
      const s = parseInt(form.stock, 10);
      payload.stock = Number.isFinite(s) ? s : 0;
      // Normalise formats: accept either string or array; strip empties
      if (typeof payload.formats === "string") {
        payload.formats = payload.formats
          .split(",")
          .map((str) => str.trim())
          .filter(Boolean);
      }
      if (!payload.formats || payload.formats.length === 0) payload.formats = ["Physical"];
      // Strip empty optional strings so backend leaves them null
      ["sku", "language", "condition", "isbn"].forEach((k) => {
        if (!payload[k]) delete payload[k];
      });
      await api.post("/books", payload);
      toast.success("Book added");
      setCreating(false);
      setForm(emptyForm);
      refresh();
    } catch (err) {
      const raw = err.response?.data?.detail;
      let msg = "Failed";
      if (typeof raw === "string") msg = raw;
      else if (Array.isArray(raw)) msg = raw.map((r) => `${(r.loc || []).slice(-1)[0] || "field"}: ${r.msg}`).join(" · ");
      else if (raw && typeof raw === "object") msg = JSON.stringify(raw);
      toast.error(msg);
    }
  };

  const del = async (id) => {
    if (!window.confirm("Remove this book?")) return;
    await api.delete(`/books/${id}`);
    toast.success("Removed");
    refresh();
  };

  const exportInventory = async () => {
    setExportingBooks(true);
    try {
      // Streaming CSV rather than the .xlsx build. An xlsx is a zip finalised
      // at the end, so nothing can be sent until the whole workbook exists;
      // on a six-figure catalogue that outlasts the edge timeout and the
      // download fails. CSV streams from the first row, so it always starts
      // immediately regardless of catalogue size.
      const resp = await api.get("/admin/books/export.csv", { responseType: "blob" });
      const stamp = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
      downloadBlob(resp.data, `inventory-${stamp}.csv`);
      toast.success("Inventory exported");
    } catch (e) {
      // responseType is "blob", so an error body is a Blob too and
      // e.response.data.detail is undefined until it is read back as text.
      let msg = "Export failed";
      try {
        const body = e?.response?.data;
        if (body && typeof body.text === "function") {
          const parsed = JSON.parse(await body.text());
          if (parsed?.detail) msg = `Export failed: ${parsed.detail}`;
        } else if (body?.detail) {
          msg = `Export failed: ${body.detail}`;
        } else if (e?.message) {
          msg = `Export failed: ${e.message}`;
        }
      } catch {
        /* keep the generic message */
      }
      toast.error(msg);
    } finally {
      setExportingBooks(false);
    }
  };

  const enrichNow = async (bookId = null) => {
    try {
      const { data } = await api.post("/admin/enrichment/run", null, { params: bookId ? { book_id: bookId } : {} });
      toast.success(`Enrichment queued for ${data.queued} ${data.queued === 1 ? "book" : "books"}`);
      setTimeout(() => refresh(), 4000);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not queue enrichment");
    }
  };

  if (loading || !user) return <div className="pt-40 text-center font-serif italic text-2xl text-[#1A1714]/60">Loading…</div>;

  return (
    <div className="pt-28 md:pt-40 max-w-[1440px] mx-auto px-6 md:px-10 pb-24" data-testid="admin-page">
      <p className="font-mono text-[10px] uppercase tracking-editorial text-[#1A1714]/60">Ch. 10 — Backroom</p>
      <h1 className="font-serif text-5xl md:text-7xl tracking-tight leading-none mt-3">Store admin</h1>

      <div className="grid grid-cols-3 gap-4 mt-10 border-y border-[#E5E1D8] py-8">
        <div><p className="font-mono text-[10px] uppercase tracking-editorial text-[#1A1714]/50">Books</p><p className="font-serif text-4xl mt-1" data-testid="stat-books">{stats.total_books}</p></div>
        <div><p className="font-mono text-[10px] uppercase tracking-editorial text-[#1A1714]/50">Orders</p><p className="font-serif text-4xl mt-1" data-testid="stat-orders">{stats.total_orders}</p></div>
        <div><p className="font-mono text-[10px] uppercase tracking-editorial text-[#1A1714]/50">Revenue (90 days)</p><p className="font-serif text-4xl mt-1" data-testid="stat-revenue">${stats.total_revenue.toFixed(2)}</p></div>
      </div>

      <div className="mt-10 flex gap-6 font-mono text-[11px] uppercase tracking-editorial">
        <button onClick={() => setTab("books")} data-testid="admin-tab-books" className={tab === "books" ? "text-[#7A2021]" : "text-[#1A1714]/60"}>Catalog</button>
        <button onClick={() => setTab("categories")} data-testid="admin-tab-categories" className={tab === "categories" ? "text-[#7A2021]" : "text-[#1A1714]/60"}>Categories</button>
        <button onClick={() => setTab("orders")} data-testid="admin-tab-orders" className={tab === "orders" ? "text-[#7A2021]" : "text-[#1A1714]/60"}>Orders</button>
        <button onClick={() => setTab("cases")} data-testid="admin-tab-cases" className={tab === "cases" ? "text-[#7A2021]" : "text-[#1A1714]/60"}>
          Cases
          {openCasesCount > 0 && (
            <span className="ml-1 inline-block bg-[#7A2021] text-[#F9F6F0] px-1.5 py-0.5 rounded-none text-[9px]" data-testid="admin-tab-cases-badge">
              {openCasesCount}
            </span>
          )}
        </button>
        <button onClick={() => setTab("gmc")} data-testid="admin-tab-gmc" className={tab === "gmc" ? "text-[#7A2021]" : "text-[#1A1714]/60"}>Feed</button>
        <button onClick={() => setTab("ingram")} data-testid="admin-tab-ingram" className={tab === "ingram" ? "text-[#7A2021]" : "text-[#1A1714]/60"}>Ingram Sync</button>
        <button onClick={() => setTab("shipping")} data-testid="admin-tab-shipping" className={tab === "shipping" ? "text-[#7A2021]" : "text-[#1A1714]/60"}>Shipping</button>
        <button onClick={() => setTab("reviews")} data-testid="admin-tab-reviews" className={tab === "reviews" ? "text-[#7A2021]" : "text-[#1A1714]/60"}>Reviews</button>
      </div>

      {tab === "gmc" && <GmcFeedPanel />}
      {tab === "ingram" && <IngramSyncPanel />}
      {tab === "shipping" && <ShippingBulkPanel />}
      {tab === "reviews" && <ReviewsPanel />}
      {tab === "categories" && <CategoryManager />}

      {tab === "books" && (
        <div className="mt-8">
          <div className="flex flex-wrap gap-3">
            <Dialog open={creating} onOpenChange={setCreating}>
              <DialogTrigger asChild>
                <Button data-testid="admin-new-book" className="rounded-none bg-[#1A1714] text-[#F9F6F0] hover:bg-[#7A2021] font-mono text-xs uppercase tracking-editorial">
                  <Plus size={14} className="mr-2" /> New book
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-[#F9F6F0] rounded-none border-[#E5E1D8] max-w-lg max-h-[90vh] overflow-y-auto">
                <DialogHeader><DialogTitle className="font-serif text-3xl">Add a book</DialogTitle></DialogHeader>
                <form onSubmit={submit} className="space-y-3" data-testid="admin-book-form">
                  <Input required placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="rounded-none" data-testid="book-form-title" />
                  <Input required placeholder="Author" value={form.author} onChange={(e) => setForm({ ...form, author: e.target.value })} className="rounded-none" data-testid="book-form-author" />
                  <Textarea required placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="rounded-none" data-testid="book-form-desc" />
                  <Input required placeholder="Cover image URL" value={form.cover_image} onChange={(e) => setForm({ ...form, cover_image: e.target.value })} className="rounded-none" data-testid="book-form-cover" />
                  <div className="grid grid-cols-2 gap-3">
                    <Input required type="number" step="0.01" placeholder="Price" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="rounded-none" data-testid="book-form-price" />
                    <Input required placeholder="Category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="rounded-none" data-testid="book-form-category" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <Input placeholder="SKU (optional)" value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} className="rounded-none" data-testid="book-form-sku" />
                    <Input placeholder="ISBN (optional)" value={form.isbn} onChange={(e) => setForm({ ...form, isbn: e.target.value })} className="rounded-none" data-testid="book-form-isbn" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <Input placeholder="Language (e.g. English)" value={form.language} onChange={(e) => setForm({ ...form, language: e.target.value })} className="rounded-none" data-testid="book-form-language" />
                    <Input placeholder="Condition (e.g. New, Like New)" value={form.condition} onChange={(e) => setForm({ ...form, condition: e.target.value })} className="rounded-none" data-testid="book-form-condition" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <Input type="number" placeholder="Pages" value={form.pages} onChange={(e) => setForm({ ...form, pages: e.target.value })} className="rounded-none" data-testid="book-form-pages" />
                    <Input type="number" placeholder="Stock" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} className="rounded-none" data-testid="book-form-stock" />
                  </div>
                  <div>
                    <p className="font-mono text-[10px] uppercase tracking-editorial text-[#1A1714]/60 mb-2">Formats</p>
                    <Input
                      data-testid="book-form-formats"
                      placeholder="e.g. Paperback, Hardcover, Calendar, Ebook, Audiobook"
                      value={Array.isArray(form.formats) ? form.formats.join(", ") : form.formats}
                      onChange={(e) => setForm({ ...form, formats: e.target.value })}
                      className="rounded-none"
                    />
                    <p className="mt-1 font-mono text-[9px] uppercase tracking-editorial text-[#1A1714]/40">
                      Comma-separated. Use any label you like.
                    </p>
                  </div>
                  <Button type="submit" className="w-full rounded-none bg-[#1A1714] text-[#F9F6F0] hover:bg-[#7A2021]" data-testid="book-form-submit">Save</Button>
                </form>
              </DialogContent>
            </Dialog>

            <Button
              onClick={() => setImporting(true)}
              data-testid="admin-import-csv"
              variant="outline"
              className="rounded-none border-[#1A1714] text-[#1A1714] hover:bg-[#1A1714] hover:text-[#F9F6F0] font-mono text-xs uppercase tracking-editorial"
            >
              <Upload size={14} className="mr-2" /> Import CSV
            </Button>

            <Button
              onClick={() => setBulkUpdating(true)}
              data-testid="admin-bulk-update"
              variant="outline"
              className="rounded-none border-[#1A1714] text-[#1A1714] hover:bg-[#1A1714] hover:text-[#F9F6F0] font-mono text-xs uppercase tracking-editorial"
            >
              <RefreshCw size={14} className="mr-2" /> Bulk update
            </Button>

            <Button
              onClick={exportInventory}
              disabled={exportingBooks}
              data-testid="admin-export-listings"
              variant="outline"
              className="rounded-none border-[#1A1714] text-[#1A1714] hover:bg-[#1A1714] hover:text-[#F9F6F0] font-mono text-xs uppercase tracking-editorial"
            >
              <Download size={14} className="mr-2" />
              {exportingBooks ? "Preparing…" : "Export listings"}
            </Button>

            <Button
              onClick={() => enrichNow()}
              data-testid="admin-enrich-pending"
              variant="outline"
              className="rounded-none border-[#1A1714] text-[#1A1714] hover:bg-[#1A1714] hover:text-[#F9F6F0] font-mono text-xs uppercase tracking-editorial"
            >
              <Sparkles size={14} className="mr-2" /> Enrich missing
            </Button>

            <Button
              onClick={() => setBulkDelete(true)}
              data-testid="admin-bulk-delete"
              variant="outline"
              className="rounded-none border-[#7A2021] text-[#7A2021] hover:bg-[#7A2021] hover:text-[#F9F6F0] font-mono text-xs uppercase tracking-editorial"
            >
              <Trash2 size={14} className="mr-2" /> Bulk delete
            </Button>
          </div>

          <ImportCSVDialog open={importing} onOpenChange={setImporting} onImported={refresh} />
          <BulkUpdateDialog open={bulkUpdating} onOpenChange={setBulkUpdating} onUpdated={refresh} />

          <div className="mt-6 flex items-center gap-3">
            <Input
              value={bookSearch}
              onChange={(e) => setBookSearch(e.target.value)}
              placeholder="Search catalog by title, author, ISBN, SKU, publisher or category"
              data-testid="admin-books-search"
              className="rounded-none border-[#E5E1D8] font-mono text-xs"
            />
            {bookSearch && (
              <Button
                onClick={() => setBookSearch("")}
                variant="outline"
                data-testid="admin-books-search-clear"
                className="rounded-none border-[#1A1714]/20 font-mono text-xs uppercase tracking-editorial"
              >
                Clear
              </Button>
            )}
          </div>
          <p className="mt-2 font-mono text-[10px] uppercase tracking-editorial text-[#1A1714]/50" data-testid="admin-books-count">
            Showing {books.length} of {stats.total_books} titles
          </p>

          <div className="mt-4 border border-[#E5E1D8]" data-testid="admin-books-list">
            <div className="grid grid-cols-12 gap-4 px-4 py-3 border-b border-[#E5E1D8] font-mono text-[10px] uppercase tracking-editorial text-[#1A1714]/60">
              <div className="col-span-4">Title</div>
              <div className="col-span-2">Category</div>
              <div className="col-span-2">Data</div>
              <div className="col-span-1 text-right">Price</div>
              <div className="col-span-1 text-right">Stock</div>
              <div className="col-span-2 text-right">Actions</div>
            </div>
            {books.map((b) => {
              const status = b.enrichment_status;
              const badge = status === "enriched"
                ? { label: "Enriched", cls: "bg-emerald-50 text-emerald-700 border-emerald-200", Icon: Check }
                : status === "not_found"
                ? { label: "Not Found", cls: "bg-red-50 text-red-700 border-red-200", Icon: XCircle }
                : status === "pending"
                ? { label: "Pending", cls: "bg-amber-50 text-amber-700 border-amber-200", Icon: Clock }
                : null;
              return (
                <div key={b.id} className="grid grid-cols-12 gap-4 px-4 py-3 border-b border-[#E5E1D8] items-center" data-testid={`admin-book-${b.id}`}>
                  <div className="col-span-4 font-serif text-lg truncate">{b.title} <span className="text-[#1A1714]/50 italic text-sm">— {b.author}</span></div>
                  <div className="col-span-2 font-mono text-xs">{b.category}</div>
                  <div className="col-span-2 flex items-center gap-2">
                    {badge ? (
                      <span
                        data-testid={`enrichment-status-${b.id}`}
                        className={`inline-flex items-center gap-1 border ${badge.cls} px-2 py-0.5 rounded-md text-[10px] font-semibold uppercase tracking-wider`}
                        title={b.enrichment_source ? `Source: ${b.enrichment_source}` : (b.enrichment_error || "")}
                      >
                        <badge.Icon size={11} /> {badge.label}
                      </span>
                    ) : (
                      <span className="font-mono text-[10px] text-[#1A1714]/40">—</span>
                    )}
                    {b.isbn && (status === "not_found" || !status) && (
                      <button
                        onClick={() => enrichNow(b.id)}
                        data-testid={`enrichment-retry-${b.id}`}
                        title="Re-run enrichment for this book"
                        className="text-[#1A1714]/60 hover:text-[#7A2021]"
                      >
                        <Sparkles size={12} />
                      </button>
                    )}
                  </div>
                  <div className="col-span-1 text-right font-mono text-xs">${b.price.toFixed(2)}</div>
                  <div className="col-span-1 text-right font-mono text-xs">{b.stock}</div>
                  <div className="col-span-2 text-right flex items-center justify-end gap-3">
                    <button
                      onClick={() => setEditingBook(b)}
                      data-testid={`admin-edit-${b.id}`}
                      className="inline-flex items-center gap-1 text-[#1A1714]/70 hover:text-[#7A2021] font-mono text-[10px] uppercase tracking-editorial"
                    >
                      <Pencil size={13} /> Edit
                    </button>
                    <button onClick={() => del(b.id)} data-testid={`admin-del-${b.id}`}>
                      <Trash2 size={14} className="text-[#1A1714]/50 hover:text-[#7A2021]" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <EditBookDialog
        book={editingBook}
        open={!!editingBook}
        onOpenChange={(o) => { if (!o) setEditingBook(null); }}
        onSaved={refresh}
      />

      <BulkDeleteDialog
        open={bulkDelete}
        onOpenChange={setBulkDelete}
        onDeleted={refresh}
      />

      {tab === "orders" && (
        <div className="mt-8 space-y-5" data-testid="admin-orders-tab">
          {/* Filters row */}
          <div className="flex flex-wrap items-end gap-3" data-testid="admin-orders-filters">
            <div className="flex-1 min-w-[220px]">
              <label className="font-mono text-[10px] uppercase tracking-editorial text-[#1A1714]/60">Search</label>
              <Input
                data-testid="orders-filter-q"
                value={orderFilters.q}
                onChange={(e) => setOrderFilters({ ...orderFilters, q: e.target.value })}
                onKeyDown={(e) => { if (e.key === "Enter") refreshOrders(orderFilters); }}
                placeholder="email · order id · session"
                className="mt-1 rounded-none border-[#E5E1D8] h-10"
              />
            </div>
            <div>
              <label className="font-mono text-[10px] uppercase tracking-editorial text-[#1A1714]/60">Status</label>
              <select
                data-testid="orders-filter-status"
                value={orderFilters.status}
                onChange={(e) => setOrderFilters({ ...orderFilters, status: e.target.value })}
                className="mt-1 h-10 border border-[#E5E1D8] rounded-none bg-white px-3 font-mono text-xs"
              >
                <option value="all">All</option>
                <option value="paid">Paid</option>
                <option value="pending">Pending</option>
                <option value="failed">Failed</option>
              </select>
            </div>
            <div>
              <label className="font-mono text-[10px] uppercase tracking-editorial text-[#1A1714]/60">From</label>
              <Input
                data-testid="orders-filter-start"
                type="date"
                value={orderFilters.start_date}
                onChange={(e) => setOrderFilters({ ...orderFilters, start_date: e.target.value })}
                className="mt-1 rounded-none border-[#E5E1D8] h-10 w-40"
              />
            </div>
            <div>
              <label className="font-mono text-[10px] uppercase tracking-editorial text-[#1A1714]/60">To</label>
              <Input
                data-testid="orders-filter-end"
                type="date"
                value={orderFilters.end_date}
                onChange={(e) => setOrderFilters({ ...orderFilters, end_date: e.target.value })}
                className="mt-1 rounded-none border-[#E5E1D8] h-10 w-40"
              />
            </div>
            <Button
              onClick={() => refreshOrders(orderFilters)}
              data-testid="orders-apply-filters"
              className="rounded-none bg-[#1A1714] text-[#F9F6F0] hover:bg-[#7A2021] font-mono text-xs uppercase tracking-editorial h-10"
            >
              <Filter size={14} className="mr-2" /> Apply
            </Button>
            <Button
              variant="outline"
              onClick={clearOrderFilters}
              data-testid="orders-clear-filters"
              className="rounded-none border-[#1A1714] font-mono text-xs uppercase tracking-editorial h-10"
            >
              Clear
            </Button>
            <Button
              variant="outline"
              onClick={resetTo30Days}
              data-testid="orders-last-30-days"
              className="rounded-none border-[#1A1714] font-mono text-xs uppercase tracking-editorial h-10"
            >
              Last 30 days
            </Button>
          </div>

          {/* Action row */}
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="font-mono text-[10px] uppercase tracking-editorial text-[#1A1714]/60" data-testid="orders-count">
              {orders.length} {orders.length === 1 ? "order" : "orders"} · {selectedOrders.size} selected
            </p>
            <div className="flex flex-wrap items-center gap-3">
              <Button
                onClick={() => setBulkTracking(true)}
                data-testid="orders-bulk-tracking-btn"
                variant="outline"
                className="rounded-none border-[#1A1714] text-[#1A1714] hover:bg-[#1A1714] hover:text-[#F9F6F0] font-mono text-xs uppercase tracking-editorial h-10"
              >
                <Truck size={14} className="mr-2" /> Bulk tracking
              </Button>
              <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  disabled={exporting}
                  data-testid="orders-export-btn"
                  className="rounded-none bg-[#1A1714] text-[#F9F6F0] hover:bg-[#7A2021] font-mono text-xs uppercase tracking-editorial h-10"
                >
                  <Download size={14} className="mr-2" />
                  {exporting ? "Preparing…" : "Export ▾"}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="rounded-none bg-[#F9F6F0] border-[#E5E1D8] w-64" data-testid="orders-export-menu">
                <DropdownMenuLabel className="font-mono text-[10px] uppercase tracking-editorial text-[#1A1714]/60">
                  Export all ({stats.total_orders})
                </DropdownMenuLabel>
                <DropdownMenuItem data-testid="export-all-csv" onClick={() => exportOrders("all", "csv")} className="rounded-none font-mono text-xs uppercase tracking-editorial">CSV</DropdownMenuItem>
                <DropdownMenuItem data-testid="export-all-xlsx" onClick={() => exportOrders("all", "xlsx")} className="rounded-none font-mono text-xs uppercase tracking-editorial">Excel (.xlsx)</DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuLabel className="font-mono text-[10px] uppercase tracking-editorial text-[#1A1714]/60">
                  Export filtered ({orders.length})
                </DropdownMenuLabel>
                <DropdownMenuItem data-testid="export-filtered-csv" onClick={() => exportOrders("filtered", "csv")} className="rounded-none font-mono text-xs uppercase tracking-editorial">CSV</DropdownMenuItem>
                <DropdownMenuItem data-testid="export-filtered-xlsx" onClick={() => exportOrders("filtered", "xlsx")} className="rounded-none font-mono text-xs uppercase tracking-editorial">Excel (.xlsx)</DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuLabel className="font-mono text-[10px] uppercase tracking-editorial text-[#1A1714]/60">
                  Export selected ({selectedOrders.size})
                </DropdownMenuLabel>
                <DropdownMenuItem
                  data-testid="export-selected-csv"
                  onClick={() => exportOrders("selected", "csv")}
                  disabled={selectedOrders.size === 0}
                  className="rounded-none font-mono text-xs uppercase tracking-editorial data-[disabled]:opacity-40"
                >CSV</DropdownMenuItem>
                <DropdownMenuItem
                  data-testid="export-selected-xlsx"
                  onClick={() => exportOrders("selected", "xlsx")}
                  disabled={selectedOrders.size === 0}
                  className="rounded-none font-mono text-xs uppercase tracking-editorial data-[disabled]:opacity-40"
                >Excel (.xlsx)</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            </div>
          </div>

          {/* Orders table */}
          <div className="border border-[#E5E1D8]" data-testid="admin-orders-list">
            {orders.length === 0 ? (
              <p className="p-8 font-serif italic text-xl text-[#1A1714]/60">No orders match these filters.</p>
            ) : (
              <>
                <div className="grid grid-cols-12 gap-4 px-4 py-3 border-b border-[#E5E1D8] font-mono text-[10px] uppercase tracking-editorial text-[#1A1714]/60 bg-[#F9F6F0]">
                  <div className="col-span-1 flex items-center">
                    <Checkbox
                      data-testid="orders-select-all"
                      checked={orders.length > 0 && selectedOrders.size === orders.length}
                      onCheckedChange={toggleAllOrders}
                    />
                  </div>
                  <div className="col-span-4">Order · Email</div>
                  <div className="col-span-2">Date</div>
                  <div className="col-span-2">Status</div>
                  <div className="col-span-1 text-right">Amount</div>
                  <div className="col-span-2 text-right">Actions</div>
                </div>
                {orders.map((o) => {
                  const isPaid = o.status === "paid";
                  const isRefunded = o.status === "refunded" || o.status === "partially_refunded";
                  return (
                    <div
                      key={o.order_id}
                      className="grid grid-cols-12 gap-4 px-4 py-3 border-b border-[#E5E1D8] items-center font-mono text-xs hover:bg-[#F9F6F0]/60 transition-colors"
                      data-testid={`admin-order-${o.order_id}`}
                    >
                      <div className="col-span-1" onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          data-testid={`orders-select-${o.order_id}`}
                          checked={selectedOrders.has(o.order_id)}
                          onCheckedChange={() => toggleOrder(o.order_id)}
                        />
                      </div>
                      <button
                        type="button"
                        onClick={() => setDetailOrderId(o.order_id)}
                        data-testid={`orders-open-${o.order_id}`}
                        className="col-span-4 text-left cursor-pointer group"
                      >
                        <p className="font-serif text-base group-hover:text-[#7A2021]">{o.email || "guest"}</p>
                        <p className="text-[10px] uppercase tracking-editorial text-[#1A1714]/50 group-hover:text-[#7A2021]/70">
                          #{o.order_id} · view details →
                        </p>
                      </button>
                      <div className="col-span-2 text-[#1A1714]/70">
                        {o.created_at ? new Date(o.created_at).toLocaleString() : "—"}
                      </div>
                      <div className="col-span-2">
                        <span className={`text-[10px] uppercase tracking-editorial ${isRefunded ? "text-[#1A1714]/50" : "text-[#7A2021]"}`}>
                          {o.status}
                        </span>
                      </div>
                      <div className="col-span-1 text-right">${(o.amount || 0).toFixed(2)}</div>
                      <div className="col-span-2 text-right" onClick={(e) => e.stopPropagation()}>
                        {isPaid && (
                          <button
                            onClick={() => refundOrder(o)}
                            disabled={refundingId === o.order_id}
                            data-testid={`orders-refund-${o.order_id}`}
                            className="inline-flex items-center gap-1 px-2 py-1 border border-[#7A2021] text-[#7A2021] hover:bg-[#7A2021] hover:text-[#F9F6F0] text-[10px] uppercase tracking-editorial disabled:opacity-50"
                          >
                            <Undo2 size={11} />
                            {refundingId === o.order_id ? "…" : "Refund"}
                          </button>
                        )}
                        {isRefunded && (
                          <span className="font-mono text-[10px] uppercase tracking-editorial text-[#1A1714]/40">
                            Refunded
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </>
            )}
          </div>

          <OrderDetailDialog
            orderId={detailOrderId}
            open={!!detailOrderId}
            onOpenChange={(o) => { if (!o) setDetailOrderId(null); }}
            onChanged={() => refreshOrders(orderFilters)}
          />

          <BulkTrackingDialog
            open={bulkTracking}
            onOpenChange={setBulkTracking}
            onUpdated={() => refreshOrders(orderFilters)}
          />
        </div>
      )}

      {tab === "cases" && (
        <div className="mt-8" data-testid="admin-cases-tab">
          <div className="flex flex-wrap gap-3 items-end mb-4">
            <div className="flex-1 min-w-[180px]">
              <label className="font-mono text-[10px] uppercase tracking-editorial text-[#1A1714]/60">Type</label>
              <select
                value={caseFilters.type}
                onChange={(e) => { const f = { ...caseFilters, type: e.target.value }; setCaseFilters(f); refreshCases(f); }}
                data-testid="admin-case-filter-type"
                className="mt-1 w-full h-10 border border-[#E5E1D8] rounded-none bg-white px-3 font-mono text-xs uppercase tracking-editorial"
              >
                <option value="all">All types</option>
                <option value="return">Return</option>
                <option value="cancellation">Cancellation</option>
                <option value="inr">Item Not Received</option>
              </select>
            </div>
            <div className="flex-1 min-w-[180px]">
              <label className="font-mono text-[10px] uppercase tracking-editorial text-[#1A1714]/60">Status</label>
              <select
                value={caseFilters.status}
                onChange={(e) => { const f = { ...caseFilters, status: e.target.value }; setCaseFilters(f); refreshCases(f); }}
                data-testid="admin-case-filter-status"
                className="mt-1 w-full h-10 border border-[#E5E1D8] rounded-none bg-white px-3 font-mono text-xs uppercase tracking-editorial"
              >
                {["all", "Open", "Awaiting Seller Response", "Awaiting Buyer Response", "Under Investigation", "Replacement Shipped", "Refunded", "Closed - Resolved", "Closed - Denied"].map((s) => (
                  <option key={s} value={s}>{s === "all" ? "All statuses" : s}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="font-mono text-[10px] uppercase tracking-editorial text-[#1A1714]/60">Show</label>
              <select
                value={caseFilters.is_open}
                onChange={(e) => { const f = { ...caseFilters, is_open: e.target.value }; setCaseFilters(f); refreshCases(f); }}
                data-testid="admin-case-filter-open"
                className="mt-1 h-10 border border-[#E5E1D8] rounded-none bg-white px-3 font-mono text-xs uppercase tracking-editorial"
              >
                <option value="all">All</option>
                <option value="open">Open only</option>
                <option value="closed">Closed only</option>
              </select>
            </div>
            <Button
              onClick={() => refreshCases(caseFilters)}
              variant="outline"
              data-testid="admin-cases-refresh"
              className="rounded-none border-[#1A1714] font-mono text-xs uppercase tracking-editorial h-10"
            >
              <RefreshCw size={12} className="mr-2" /> Refresh
            </Button>
          </div>

          <div className="border border-[#E5E1D8]" data-testid="admin-cases-list">
            <div className="grid grid-cols-12 gap-3 px-4 py-3 border-b border-[#E5E1D8] font-mono text-[10px] uppercase tracking-editorial text-[#1A1714]/60">
              <div className="col-span-1">Case #</div>
              <div className="col-span-2">Type</div>
              <div className="col-span-2">Order</div>
              <div className="col-span-3">Buyer</div>
              <div className="col-span-2">Status</div>
              <div className="col-span-1">Refunded</div>
              <div className="col-span-1">Opened</div>
            </div>
            {cases.length === 0 ? (
              <p className="p-6 font-serif italic text-lg text-[#1A1714]/60" data-testid="admin-cases-empty">
                No cases match these filters.
              </p>
            ) : (
              cases.map((c) => {
                const closedC = ["Refunded", "Closed - Resolved", "Closed - Denied"].includes(c.status);
                return (
                  <button
                    key={c.id}
                    onClick={() => setSelectedCaseId(c.id)}
                    data-testid={`admin-case-row-${c.id}`}
                    className="w-full grid grid-cols-12 gap-3 px-4 py-3 border-b border-[#E5E1D8] items-center text-left hover:bg-[#F3EFE6] transition-colors"
                  >
                    <div className="col-span-1 font-mono text-xs text-[#7A2021]">#{c.case_number}</div>
                    <div className="col-span-2 font-mono text-xs">
                      {c.type === "return" ? "Return" : c.type === "cancellation" ? "Cancellation" : "Item not received"}
                    </div>
                    <div className="col-span-2 font-mono text-xs">#{c.order_id}</div>
                    <div className="col-span-3 font-mono text-xs text-[#1A1714]/70 truncate">{c.user_email}</div>
                    <div className="col-span-2">
                      <span className={`inline-block font-mono text-[10px] uppercase tracking-editorial border px-2 py-1 ${closedC ? "border-[#1A1714]/40 text-[#1A1714]/60" : "border-[#7A2021] text-[#7A2021]"}`}>
                        {c.status}
                      </span>
                    </div>
                    <div className="col-span-1 font-mono text-xs">
                      {c.total_refunded ? `$${Number(c.total_refunded).toFixed(2)}` : "—"}
                    </div>
                    <div className="col-span-1 font-mono text-[10px] text-[#1A1714]/60">
                      {new Date(c.created_at).toLocaleDateString()}
                    </div>
                  </button>
                );
              })
            )}
          </div>

          <AdminCaseDetailDialog
            caseId={selectedCaseId}
            open={!!selectedCaseId}
            onOpenChange={(o) => { if (!o) setSelectedCaseId(null); }}
            onChanged={() => refreshCases(caseFilters)}
          />
        </div>
      )}
    </div>
  );
}
